import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import { TooltipButton } from './tooltip-button'
import { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding'

export interface ITableSummaryStat {
  /** Short caption above the figure, e.g. "Records", "Total emissions", "Spend". */
  label: string
  /** The figure, already formatted. */
  value: string
  /** Optional explanation shown behind an info icon next to the label. */
  hint?: string
}

/**
 * The standard totals strip for a list: a row of labelled figures (count, sums) that TableDisplay
 * renders on the left of its pagination bar, so the whole-list totals read next to "1-10 of N".
 * Figures are whole-list totals, never the page on screen: pass them from the API, not from rows.
 */
export const TableSummaryStats: React.FC<{ items: ITableSummaryStat[] }> = ({ items }) => {
  const visible = items.filter((item) => item && item.value)
  if (visible.length === 0) return null
  return (
    <Stack
      direction='row'
      alignItems='center'
      flexWrap='wrap'
      divider={<Divider orientation='vertical' flexItem sx={{ my: 0.75 }} />}
      sx={{ columnGap: 2.5, rowGap: 1, py: 1 }}
      aria-label='Totals for every record matching the current filters'
    >
      {visible.map((item) => (
        <Stack key={item.label} spacing={0} sx={{ minWidth: 0 }}>
          <Stack direction='row' alignItems='center' spacing={0.25}>
            <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap', lineHeight: 1.4 }}>
              {item.label}
            </Typography>
            {item.hint && (
              <TooltipButton noPadding placement='top' text={item.hint} />
            )}
          </Stack>
          <Typography
            variant='body1'
            sx={{ fontFamily: BRAND_DISPLAY_FONT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', lineHeight: 1.3 }}
          >
            {item.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}
